import React from 'react';
import { Link } from 'react-router-dom';

export const ClaimsEvidencePolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans">
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/official.png" alt="Verisure" className="h-9 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4 text-xs font-bold">
            <Link to="/about" className="text-slate-400 hover:text-white transition">About Us</Link>
            <Link to="/terms" className="text-slate-400 hover:text-white transition">Terms</Link>
            <Link to="/login" className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-xl">
              Sign In
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold rounded-full mb-3">
            Forensic Evidence Guidelines
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white">Insurance Claims Evidence Policy</h1>
          <p className="text-slate-400 text-sm mt-2">Standards for Tamper-Proof Breakdown Records & Insurer Auditing</p>
        </div>

        <div className="space-y-8 text-sm leading-relaxed text-slate-300 bg-slate-900/60 border border-slate-800 p-6 md:p-10 rounded-3xl">
          <section>
            <h2 className="text-lg font-bold text-white mb-2 text-orange-400">1. Objective & Scope</h2>
            <p>
              This policy defines the evidentiary standards required for repair logs to qualify as <em>Insurer-Audit-Ready</em> on the Verisure platform. By enforcing structured forensic documentation at the point of repair, Verisure eliminates ambiguous breakdown claims and enables insurance underwriters to process legitimate claims with zero friction.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2 text-orange-400">2. Photographic Evidence Standard</h2>
            <p>
              To maintain audit validity, every repair log submitted after an equipment breakdown must include:
            </p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 text-slate-300">
              <li><strong>Faulty Component Photo:</strong> Clear image showing the worn, burnt, or damaged component <em>in-situ</em> before extraction.</li>
              <li><strong>Replacement Component Photo:</strong> Clear image of the new replacement part showing manufacturer serial numbers and packaging authenticity seals.</li>
              <li><strong>Post-Service Operating Status:</strong> Photographic or telemetry evidence confirming the equipment has returned to normal operating condition.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2 text-orange-400">3. Parts Authenticity Classification</h2>
            <p>
              All replacement parts must be explicitly categorized by the certified technician:
            </p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 text-slate-300">
              <li><strong>OEM Certified (Original Equipment Manufacturer):</strong> Direct replacement from original manufacturer with full warranty compatibility.</li>
              <li><strong>Approved Aftermarket:</strong> High-grade compatible component meeting or exceeding OEM technical specifications.</li>
              <li><strong>Generic / Emergency Temporary:</strong> Temporary component installed strictly to prevent catastrophic stoppage; flagged for scheduled replacement.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2 text-orange-400">4. Insurer Audit Protocol</h2>
            <p>
              Licensed insurance adjusters and claims handlers can independently audit any maintenance dossier by scanning the equipment's physical Verisure QR tag or entering the unique verification code on the public ledger. Verisure provides instant verification of:
            </p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 text-slate-300">
              <li>Full historical maintenance cadence prior to the breakdown date.</li>
              <li>Technician KYC credentials and qualification level.</li>
              <li>Geographic GPS stamping matching the insured property address.</li>
            </ul>
          </section>
        </div>
      </main>

      <footer className="py-10 border-t border-slate-800 text-center text-xs text-slate-500">
        <p>&copy; {new Date().getFullYear()} VERISURE • Equipment Maintenance Tracking & Insurance Evidence.</p>
      </footer>
    </div>
  );
};
